import { buildQueryString } from '../utils/buildQueryString'
import { createRestApiClient } from '../utils/createRestApiClient'
import { sign } from '../utils/sign'

const BASE_URL = 'https://api.mexc.com'
const BASE_FUTURES_URL = 'https://contract.mexc.com'
const RECV_WINDOW = 5000

const createAuthHeaders = async (params: {
  /** 認証用 API Key */
  apiKey: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
}): Promise<Record<string, string>> => {
  const { apiKey, createHeaders } = params

  // 強制適用する request headers 作成
  const overrideHeaders = createHeaders ? await createHeaders() : {}

  return {
    'Content-Type': 'application/json',
    'X-MEXC-APIKEY': apiKey,
    ...overrideHeaders,
  }
}

const createFuturesAuthHeaders = async (params: {
  /** 認証用 API Key */
  apiKey: string
  /** 認証用 API Secret */
  apiSecret: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  dataString: string
  timestamp: string
}): Promise<Record<string, string>> => {
  const { apiKey, apiSecret, createHeaders, dataString, timestamp } = params

  // apiKey + timestamp + dataString
  const signTarget = `${apiKey}${timestamp}${dataString}`

  // HMAC-SHA256署名の生成とhexdigest化
  const signature = await sign({
    algorithm: 'sha256',
    payload: signTarget,
    secret: apiSecret,
    encoding: 'hex',
  })

  // 強制適用する request headers 作成
  const overrideHeaders = createHeaders ? await createHeaders() : {}

  return {
    ApiKey: apiKey,
    'Content-Type': 'application/json',
    'Request-Time': timestamp,
    Signature: signature,
    ...overrideHeaders,
  }
}

export const createClient = ({
  apiKey,
  apiSecret,
  createHeaders,
  proxy,
  type = 'spot',
}: {
  /** 認証用 API Key */
  apiKey: string
  /** 認証用 API Secret */
  apiSecret: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  /** request を proxy する場合の proxy base URL */
  proxy?: string
  type?: 'spot' | 'futures'
}) =>
  createRestApiClient({
    buildRequestData: async ({ method, path, query, body }) => {
      const timestamp = Date.now().toString()
      const recvWindow = RECV_WINDOW.toString()

      switch (type) {
        case 'futures': {
          if (method === 'POST') {
            throw new Error('POST method is not supported for futures API')
          }
          const dataString = buildQueryString(query)
          const headers = await createFuturesAuthHeaders({
            apiKey,
            apiSecret,
            createHeaders,
            dataString,
            timestamp,
          })
          return {
            url: `${BASE_FUTURES_URL}${path}${dataString ? `?${dataString}` : ''}`,
            headers,
          }
        }
        case 'spot': {
          // パラメータの準備
          const params = {
            ...query,
            ...body,
            recvWindow,
            timestamp,
          }

          const dataString = buildQueryString(params)

          // HMAC-SHA256署名の生成
          const signature = await sign({
            algorithm: 'sha256',
            payload: dataString,
            secret: apiSecret,
            encoding: 'hex',
          })

          const headers = await createAuthHeaders({
            apiKey,
            createHeaders,
          })

          if (method === 'GET') {
            // GETの場合: query parametersにsignatureを追加
            const queryWithSignature = buildQueryString({
              ...params,
              signature,
            })
            const url = `${BASE_URL}${path}?${queryWithSignature}`
            return {
              url,
              headers,
            }
          }
          // POSTの場合: bodyにsignatureを追加
          const bodyWithSignature = buildQueryString({ ...params, signature })
          const url = `${BASE_URL}${path}`
          return {
            url,
            headers,
            body: bodyWithSignature,
          }
        }
        default: {
          throw new Error(`Unsupported type: ${type satisfies never}`)
        }
      }
    },
    getDelayForRetry: ({ statusCode, errorMessage }) => {
      // 400: Timestamp for this request is outside of the recvWindow エラーは 1秒待機してリトライ
      if (
        statusCode === 400 &&
        errorMessage.includes(
          'Timestamp for this request is outside of the recvWindow',
        )
      ) {
        return 1000
      }
      // 401 エラーは 1秒待機してリトライ
      if (statusCode === 401) {
        return 1000
      }
      // 429 エラーは 10秒待機してリトライ
      if (statusCode === 429) {
        return 10_000
      }
      // 5xx エラーは 1秒待機してリトライ
      if (statusCode >= 500) {
        return 1000
      }
      // それ以外のエラーはリトライしない
      return undefined
    },
    proxy,
  })
